import { z } from 'zod';

export const TopProductSchema = z.object({
  id: z.union([z.string(), z.number()]).transform((value) => String(value)).optional(),
  asin: z.string().optional(),
  upc: z.string().optional(),
  name: z.string().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  marketplace: z.string().optional().default('Unknown'),
  price: z.number().optional().or(z.string().transform((value) => Number(value) || 0)),
  original_price: z.number().optional(),
  discount_percentage: z.number().optional(),
  currency: z.string().optional().default('USD'),
  rating: z.number().optional(),
  reviews: z.number().optional(),
  image_url: z.string().optional(),
  images: z.array(z.object({
    url: z.string(),
    type: z.string().optional()
  })).optional(),
  url: z.string().optional(),
  seller: z.object({
    name: z.string().optional(),
    rating: z.number().optional(),
    fulfilled_by: z.string().optional()
  }).optional(),
  availability: z.object({
    in_stock: z.boolean().optional(),
    quantity: z.number().optional(),
    ships_from: z.string().optional(),
    delivery_days: z.number().optional()
  }).optional(),
  performance: z.number().optional(),
  scans: z.number().optional(),
}).transform((data) => ({
  ...data,
  id: data.id || data.asin || data.upc || 'unknown',
  image_url: data.image_url || (data.images && data.images.length > 0 ? data.images[0].url : undefined)
}));

export const MetricsSchema = z.object({
  total_agents: z.number().optional(),
  active_agents: z.number().optional(),
  status: z.string().optional(),
  total_scans_today: z.number().optional(),
  monthly_profit: z.number().optional(),
  products_analyzed: z.number().optional(),
  system_uptime: z.number().optional(),
  revenue_trend: z
    .array(
      z.object({
        date: z.string().optional(),
        name: z.string().optional(),
        revenue: z.number().or(z.string().transform((value) => Number(value) || 0)),
        profit: z.number().optional(),
      }),
    )
    .optional()
    .default([]),
  top_products: z.array(TopProductSchema).optional().default([]),
});

export const AgentSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  status: z.string().optional(),
  marketplace: z.string().optional(),
  last_run: z.string().nullable().optional(),
  accuracy: z.number().optional(),
  success_rate: z.number().optional(),
  performance: z.number().optional(),
  scans: z.number().optional(),
  products_monitored: z.number().optional(),
  total_scans: z.number().optional(),
});

export const AgentsResponseSchema = z.object({
  total_agents: z.number().optional(),
  active_agents: z.number().optional(),
  agents: z.array(AgentSchema).default([]),
});

export const ScraperSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  status: z.string().optional(),
  marketplace: z.string().optional(),
  last_run: z.string().nullable().optional(),
  success_rate: z.number().optional(),
  accuracy: z.number().optional(),
  performance: z.number().optional(),
  items_scraped: z.number().optional(),
  items_processed: z.number().optional(),
  items: z.number().optional(),
  next_run: z.string().nullable().optional(),
});

export const ScrapersResponseSchema = z.object({
  total_scrapers: z.number().optional(),
  active_scrapers: z.number().optional(),
  scrapers: z.array(ScraperSchema).default([]),
});

export const AlertSchema = z.object({
  id: z.string().optional(),
  type: z.string().optional(),
  level: z.string().optional(),
  message: z.string().optional(),
  timestamp: z.string().optional(),
});

export const AlertsResponseSchema = z.array(AlertSchema).default([]);

export const ProductsSearchSchema = z.object({
  success: z.boolean().optional().default(true),
  query: z.string().optional(),
  products: z.array(TopProductSchema).default([]),
  total: z.number().optional(),
  total_results: z.number().optional(),
  returned_results: z.number().optional(),
  filters_applied: z.object({
    marketplace: z.string().optional(),
    min_price: z.number().nullable().optional(),
    max_price: z.number().nullable().optional(),
    min_rating: z.number().nullable().optional()
  }).optional(),
  sort: z.string().optional(),
  pagination: z.object({
    offset: z.number().optional(),
    limit: z.number().optional(),
    has_more: z.boolean().optional()
  }).optional(),
  timestamp: z.string().optional()
}).transform((data) => ({
  ...data,
  total: data.total || data.total_results || data.products.length
}));

export const BarcodeScanSchema = z.object({
  product: TopProductSchema.optional(),
  products: z.array(TopProductSchema).optional(),
});
